import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Button, ButtonGroup, Dialog } from '@mui/material';
import { MapContainer, TileLayer } from 'react-leaflet';
import WindBarbMarker from './WindBarbMarker';
import LightningStrikeMarker from './LightningStrikeMarker';
import WildfireMarker from './WildfireMarker';
import { useLatestData } from '../hooks/useLatestData';
import { useLatestLightningData } from '../hooks/useLatestLightningData';
import { useWildfireData } from '../hooks/useWildfireData';
import { formatDateTime, kphToMph } from '../utils/utils';
import { differenceInMinutes } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import MapIcon from '@mui/icons-material/Map';
import SatelliteIcon from '@mui/icons-material/Satellite';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { supabase } from '../supabaseClient';
import 'leaflet/dist/leaflet.css';
import './CurrentConditions.css';

const DEFAULT_FIRE_ALERT_DISTANCE_MILES = 15;
const MAX_FIRE_ALERT_DISTANCE_MILES = 15;

const CurrentConditions = ({
  site,
  latitude,
  longitude,
  timezone,
  refreshInterval,
  latestData: latestDataFromProps,
  latestDataLoading: latestDataLoadingFromProps,
  latestDataError: latestDataErrorFromProps,
  lightningData: lightningDataFromProps,
  lightningLoading: lightningLoadingFromProps,
  lightningError: lightningErrorFromProps,
}) => {
  const hasSharedLatestData = latestDataFromProps !== undefined;
  const hasSharedLightningData = lightningDataFromProps !== undefined;

  const {
    latestData: hookLatestData,
    loading: hookWeatherLoading,
    error: hookWeatherError,
  } = useLatestData(site, refreshInterval, !hasSharedLatestData);
  const {
    lightningData: hookLightningData,
    loading: hookLightningLoading,
    error: hookLightningError,
  } = useLatestLightningData(site, refreshInterval, !hasSharedLightningData);
  const { wildfireData, loading: wildfireLoading, getFireAlertLevel } = useWildfireData(site, refreshInterval);

  const latestData = hasSharedLatestData ? latestDataFromProps : hookLatestData;
  const weatherLoading = hasSharedLatestData ? Boolean(latestDataLoadingFromProps) : hookWeatherLoading;
  const weatherError = hasSharedLatestData ? latestDataErrorFromProps : hookWeatherError;
  const lightningData = hasSharedLightningData ? lightningDataFromProps : hookLightningData;
  const lightningLoading = hasSharedLightningData ? Boolean(lightningLoadingFromProps) : hookLightningLoading;
  const lightningError = hasSharedLightningData ? lightningErrorFromProps : hookLightningError;
  const [mapLayer, setMapLayer] = useState('street'); // 'street' or 'satellite'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fireAlertDistance, setFireAlertDistance] = useState(DEFAULT_FIRE_ALERT_DISTANCE_MILES);
  const mapRef = useRef(null);
  const fullscreenMapRef = useRef(null);

  // Fetch fire alert distance setting
  useEffect(() => {
    let cancelled = false;

    const fetchAlertSettings = async () => {
      if (!site) return;

      try {
        const { data: alertData } = await supabase
          .from('alert_thresholds')
          .select('fire_alert_distance_miles')
          .eq('site_name', site)
          .single();

        if (cancelled) {
          return;
        }

        if (alertData?.fire_alert_distance_miles != null) {
          setFireAlertDistance(Math.min(Math.max(alertData.fire_alert_distance_miles, 0), MAX_FIRE_ALERT_DISTANCE_MILES));
        } else {
          setFireAlertDistance(DEFAULT_FIRE_ALERT_DISTANCE_MILES);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Error fetching alert settings:', error);
        setFireAlertDistance(DEFAULT_FIRE_ALERT_DISTANCE_MILES);
      }
    };

    fetchAlertSettings();
    return () => {
      cancelled = true;
    };
  }, [site]);

  if (weatherLoading || lightningLoading || wildfireLoading) {
    return (
      <Card sx={{ height: { xs: 760, md: 460 } }}>
        <CardContent sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2 }}>
          <CircularProgress sx={{ color: 'var(--color-brand)' }} />
        </CardContent>
      </Card>
    );
  }

  if (weatherError || lightningError) {
    return (
      <Card sx={{ height: { xs: 760, md: 460 } }}>
        <CardContent sx={{ padding: 2 }}>
          <Typography color="error">{weatherError || lightningError}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!latestData?.time_utc) {
    return (
      <Card sx={{ height: { xs: 760, md: 460 } }}>
        <CardContent sx={{ padding: 2 }}>
          <Typography>No weather data available for this site</Typography>
        </CardContent>
      </Card>
    );
  }


  const timeUtc = latestData.time_utc.endsWith('Z') ? latestData.time_utc : `${latestData.time_utc}Z`;
  const timestamp = new Date(timeUtc).getTime();
  const formattedDateTime = formatDateTime(timestamp, timezone);
  const speed = kphToMph(latestData.wind_speed_kph);
  const gust = latestData.wind_gust_kph != null ? kphToMph(latestData.wind_gust_kph) : null;
  const pm2_5 = latestData.pm2_5_ugm3;
  const pm10 = latestData.pm10_ugm3;

  const now = new Date();
  const lightningRows = Array.isArray(lightningData) ? lightningData : [];
  const recentStrikes = lightningRows.filter(strike => {
    if (!strike?.time_utc || strike.distance_km == null) {
      return false;
    }
    const strikeTime = new Date(strike.time_utc.endsWith('Z') ? strike.time_utc : `${strike.time_utc}Z`);
    const ageMinutes = differenceInMinutes(now, strikeTime);
    const distanceMiles = strike.distance_km * 0.621371; // Convert km to miles
    return ageMinutes <= 30 && distanceMiles <= 20;
  });
  const nearestStrike = recentStrikes.length > 0
    ? recentStrikes.reduce((prev, curr) => 
        curr.distance_km < prev.distance_km ? curr : prev, recentStrikes[0])
    : null;
  const nearestStrikeTime = nearestStrike ? formatDateTime(new Date(nearestStrike.time_utc + (nearestStrike.time_utc.endsWith('Z') ? '' : 'Z')).getTime(), timezone) : null;
  const nearestStrikeDistanceMiles = nearestStrike ? (nearestStrike.distance_km * 0.621371).toFixed(1) : 'None within 20 mi';

  const defaultZoom = 10;

  const mapActionButtonSx = {
    backgroundColor: 'rgba(18, 59, 48, 0.82)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    minWidth: 'auto',
    width: 34,
    height: 34,
    '&:hover': {
      backgroundColor: 'rgba(12, 42, 34, 0.92)',
      borderColor: 'rgba(255, 255, 255, 0.35)',
    },
  };

  const fullscreenMapActionButtonSx = {
    ...mapActionButtonSx,
    width: 42,
    height: 42,
  };


  return (
    <>
      <Card sx={{ height: { xs: 760, md: 460 }, overflow: 'hidden' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            flexShrink: 0,
            borderBottom: '1px solid rgba(17, 47, 38, 0.12)',
            background:
              'linear-gradient(120deg, rgba(31, 92, 74, 0.1), rgba(255, 255, 255, 0.9) 55%, rgba(227, 83, 2, 0.08))',
          }}
        >
          <Typography variant="h6" sx={{ color: 'var(--color-brand-dark)' }}>Current Conditions</Typography>
          <Tooltip
            title={
              <Box sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                <strong>Wind Arrow:</strong> Points in the direction the wind is blowing. Its size scales with wind speed (larger = faster).<br />
                <br />
                <strong>Wind Colors (mph):</strong><br />
                <span style={{ color: '#2ecc71' }}>■</span> 0-15: Low wind<br />
                <span style={{ color: '#f1c40f' }}>■</span> 15-20: Moderate wind<br />
                <span style={{ color: '#e67e22' }}>■</span> 20-25: Strong wind<br />
                <span style={{ color: '#e74c3c' }}>■</span> 25+: High wind<br />
                <br />
                <strong>Lightning Monitoring:</strong> Map shows strikes within 30 min and 20 mi. Alert bands are Yellow {">"}12-18 mi, Orange {">"}6-12 mi, and Red ≤6 mi with a 30-minute hold.<br />
                <br />
                <strong>Fire Icons:</strong> Flame symbols show active wildfires up to {MAX_FIRE_ALERT_DISTANCE_MILES} mi. Click on fire icon or perimeter for details.<br />
                <br />
                <strong>Fire Colors:</strong><br />
                <span style={{ color: '#dc2626' }}>●</span> Critical: 0-5 mi from site<br />
                <span style={{ color: '#ea580c' }}>●</span> Warning: 6-10 mi from site<br />
                <span style={{ color: '#eab308' }}>●</span> Monitor: 11-15 mi from site<br />
                <br />
                <strong>Fullscreen:</strong> Click the fullscreen button for detailed map view.
              </Box>
            }
            arrow
          >
            <IconButton sx={{ color: 'var(--color-brand)' }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, p: 1.5, minHeight: 0, gap: 1.5 }}>
          <Box sx={{ width: { xs: '100%', md: '55%' }, height: { xs: '52%', md: '100%' }, minHeight: 240 }}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <ButtonGroup
                orientation="vertical"
                size="small"
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  left: 8, 
                  zIndex: 1001,
                  backgroundColor: 'rgba(8, 26, 21, 0.32)',
                  borderRadius: 1.25
                }}
              >
                <Button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  sx={mapActionButtonSx}
                >
                  {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </Button>
                <Button
                  onClick={() => mapRef.current?.zoomIn()}
                  sx={mapActionButtonSx}
                >
                  <ZoomInIcon fontSize="small" />
                </Button>
                <Button
                  onClick={() => mapRef.current?.zoomOut()}
                  sx={mapActionButtonSx}
                >
                  <ZoomOutIcon fontSize="small" />
                </Button>
                <Button
                  onClick={() => setMapLayer(mapLayer === 'street' ? 'satellite' : 'street')}
                  sx={mapActionButtonSx}
                >
                  {mapLayer === 'street' ? <SatelliteIcon fontSize="small" /> : <MapIcon fontSize="small" />}
                </Button>
              </ButtonGroup>
              <MapContainer
                center={[latitude, longitude]}
                zoom={defaultZoom}
                minZoom={6}
                maxZoom={18}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
                ref={mapRef}
                zoomControl={false}
              >
                {mapLayer === 'street' ? (
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    className="grayscale-tile-layer"
                  />
                ) : (
                  <>
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution='© <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
                      className="satellite-tile-layer"
                    />
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                      attribution=''
                      className="satellite-tile-layer"
                    />
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                      attribution=''
                      className="satellite-tile-layer"
                    />
                  </>
                )}
                <WindBarbMarker
                  position={[latitude, longitude]}
                  speed={speed}
                  direction={latestData.wind_direction || 'N'}
                />
                {lightningData.map((strike, index) => (
                  <LightningStrikeMarker
                    key={index}
                    position={[strike.latitude, strike.longitude]}
                    timestamp={strike.time_utc}
                    timezone={timezone}
                    distance_km={strike.distance_km}
                  />
                ))}
                {wildfireData
                  .filter(fire => fire.distance_miles <= fireAlertDistance)
                  .map((fire, index) => (
                    <WildfireMarker
                      key={`fire-${fire.fire_id}-${index}`}
                      fire={fire}
                      alertLevel={getFireAlertLevel(fire)}
                    />
                  ))}
              </MapContainer>
            </Box>
          </Box>
          <Box sx={{ width: { xs: '100%', md: '45%' }, height: { xs: '48%', md: '100%' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                border: '1px solid var(--color-border)',
                boxShadow: 'none',
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap', width: '45%', color: 'var(--color-brand-dark)' }}><strong>Date-Time</strong></TableCell>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>{formattedDateTime || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap', color: 'var(--color-brand-dark)' }}><strong>Wind Speed</strong></TableCell>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>{speed.toFixed(1)} mph</TableCell>
                  </TableRow>
                  {gust !== null && (
                    <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                      <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap', color: 'var(--color-brand-dark)' }}><strong>Wind Gust</strong></TableCell>
                      <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>{gust.toFixed(1)} mph</TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap', color: 'var(--color-brand-dark)' }}><strong>PM2.5</strong></TableCell>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>{pm2_5 != null ? pm2_5.toFixed(1) : 'N/A'} μg/m³</TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap', color: 'var(--color-brand-dark)' }}><strong>PM10</strong></TableCell>
                    <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>{pm10 != null ? pm10.toFixed(1) : 'N/A'} μg/m³</TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      <strong>Nearest Lightning</strong><br />
                      <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>within 30 min, monitored to 20 mi</span>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      {nearestStrike ? (
                        <>
                          {nearestStrikeTime}<br />
                          <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>{nearestStrikeDistanceMiles} mi from site</span>
                        </>
                      ) : (
                        <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>None within 20 mi</span>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(31, 92, 74, 0.04)' } }}>
                    <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      <strong>Active Wildfires</strong><br />
                      <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>within {fireAlertDistance} mi</span>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const firesWithinAlertDistance = wildfireData.filter(fire =>
                          fire.distance_miles <= fireAlertDistance
                        );
                        const nearestFire = firesWithinAlertDistance.length > 0 ?
                          firesWithinAlertDistance.reduce((nearest, fire) =>
                            (!nearest || fire.distance_miles < nearest.distance_miles) ? fire : nearest
                          ) : null;

                        if (nearestFire) {
                          const alertLevel = getFireAlertLevel(nearestFire);
                          const alertColors = {
                            critical: '#dc2626',
                            warning: '#ea580c',
                            monitor: '#eab308'
                          };

                          return (
                            <>
                              <span style={{ color: alertColors[alertLevel], fontWeight: 'bold' }}>
                                {nearestFire.fire_name || 'Unnamed Fire'}
                              </span><br />
                              <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>
                                {nearestFire.distance_miles.toFixed(1)} mi - {alertLevel}
                              </span>
                            </>
                          );
                        } else {
                          return <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>None within {fireAlertDistance} mi</span>;
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
    
    <Dialog
      fullScreen
      open={isFullscreen}
      onClose={() => setIsFullscreen(false)}
      sx={{ '& .MuiDialog-paper': { backgroundColor: '#f0f5f3' } }}
    >
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            background: 'linear-gradient(120deg, rgba(22, 63, 52, 0.97), rgba(31, 92, 74, 0.95))',
            boxShadow: 1,
          }}
        >
          <Typography variant="h4" sx={{ color: '#ffffff' }}>Current Conditions - {site}</Typography>
          <Button
            onClick={() => setIsFullscreen(false)}
            sx={{ color: '#ffffff' }}
            startIcon={<FullscreenExitIcon />}
          >
            Exit Fullscreen
          </Button>
        </Box>
        
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <ButtonGroup
            orientation="vertical"
            size="small"
            sx={{ 
              position: 'absolute', 
              top: 16, 
              left: 16, 
              zIndex: 1001,
              backgroundColor: 'rgba(8, 26, 21, 0.32)',
              borderRadius: 1.25
            }}
          >
            <Button
              onClick={() => setIsFullscreen(false)}
              sx={fullscreenMapActionButtonSx}
            >
              <FullscreenExitIcon />
            </Button>
            <Button
              onClick={() => fullscreenMapRef.current?.zoomIn()}
              sx={fullscreenMapActionButtonSx}
            >
              <ZoomInIcon />
            </Button>
            <Button
              onClick={() => fullscreenMapRef.current?.zoomOut()}
              sx={fullscreenMapActionButtonSx}
            >
              <ZoomOutIcon />
            </Button>
            <Button
              onClick={() => setMapLayer(mapLayer === 'street' ? 'satellite' : 'street')}
              sx={fullscreenMapActionButtonSx}
            >
              {mapLayer === 'street' ? <SatelliteIcon /> : <MapIcon />}
            </Button>
          </ButtonGroup>
          
          <MapContainer
            center={[latitude, longitude]}
            zoom={defaultZoom}
            minZoom={3}
            maxZoom={18}
            scrollWheelZoom={true}
            style={{ height: '100%', width: '100%' }}
            ref={fullscreenMapRef}
            zoomControl={false}
          >
            {mapLayer === 'street' ? (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                className="grayscale-tile-layer"
              />
            ) : (
              <>
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='© <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
                  className="satellite-tile-layer"
                />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                  attribution=''
                  className="satellite-tile-layer"
                />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  attribution=''
                  className="satellite-tile-layer"
                />
              </>
            )}
            <WindBarbMarker
              position={[latitude, longitude]}
              speed={speed}
              direction={latestData.wind_direction || 'N'}
            />
            {lightningData.map((strike, index) => (
              <LightningStrikeMarker
                key={index}
                position={[strike.latitude, strike.longitude]}
                timestamp={strike.time_utc}
                timezone={timezone}
                distance_km={strike.distance_km}
              />
            ))}
            {wildfireData
              .filter(fire => fire.distance_miles <= fireAlertDistance)
              .map((fire, index) => (
                <WildfireMarker
                  key={`fire-fullscreen-${fire.fire_id}-${index}`}
                  fire={fire}
                  alertLevel={getFireAlertLevel(fire)}
                />
              ))}
          </MapContainer>
        </Box>
      </Box>
    </Dialog>
    </>
  );
};

export default CurrentConditions;
